import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { Circle, Eye, Radio, ServerCrash } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';

import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Sparkline } from '../../components/Sparkline';
import { StatusBadge } from '../../components/StatusBadge';
import { useAuth } from '../auth/AuthProvider';
import { listMediaNodes, mediaNodesKey, uploadFailing } from '../platform/mediaNodes';
import { useTimeFormat } from '../../lib/useTimeFormat';
import { usePageVisible } from '../live/usePageVisible';
import {
  formatBitrate,
  formatBytes,
  formatUptime,
  describeAgent,
  getMediaStatus,
  getMediaViewers,
  loadLabel,
  loadTone,
  mediaStatusKey,
  mediaViewersKey,
  shownOf,
  summarizeLoops,
  tenantName,
  transportLabels,
  viewerKindLabels,
  viewerName,
  type MediaStatus,
  type ThreadLoad,
} from './media';

const refreshMs = 5000;
/** Five minutes of history at the refresh rate. */
const historySize = 60;

interface Sample {
  at: number;
  bytesPerSecond: number;
  viewers: number;
}

const timeFormat = new Intl.DateTimeFormat('pt-BR', { timeStyle: 'medium' });

/**
 * Media-server usage: the tenant's own streams plus aggregate node load.
 * Polls while the page is visible; history lives only in this view.
 */
export function MediaServerPanel() {
  const { request, can } = useAuth();
  const canNodes = can('platform.media_nodes');
  const visible = usePageVisible(0);
  const q = useQuery({
    queryKey: mediaStatusKey,
    queryFn: () => getMediaStatus(request),
    refetchInterval: visible ? refreshMs : false,
    refetchIntervalInBackground: false,
  });
  // The nodes whose recordings are not leaving them (0.60.0): only for
  // whoever can open the node's page.
  const nodes = useQuery({
    queryKey: mediaNodesKey,
    queryFn: () => listMediaNodes(request),
    enabled: canNodes,
    refetchInterval: visible ? 15_000 : false,
    refetchIntervalInBackground: false,
  });
  const stuck = canNodes ? (nodes.data?.items.filter(uploadFailing) ?? []) : [];

  const [history, setHistory] = useState<Sample[]>([]);
  const lastAt = useRef(0);
  useEffect(() => {
    const d = q.data;
    if (!d || d.status !== 'up' || q.dataUpdatedAt === lastAt.current) return;
    lastAt.current = q.dataUpdatedAt;
    const sample: Sample = {
      at: q.dataUpdatedAt,
      bytesPerSecond: d.streams.reduce((a, s) => a + s.bytes_per_second, 0),
      viewers: d.streams.reduce((a, s) => a + s.viewers, 0),
    };
    setHistory((h) => [...h.slice(-(historySize - 1)), sample]);
  }, [q.data, q.dataUpdatedAt]);

  return (
    <section
      aria-labelledby="media-heading"
      className="overflow-hidden rounded-lg border border-line bg-surface"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-2.5">
        <div className="flex items-center gap-3">
          <h3 id="media-heading" className="text-[13px] font-medium">
            Servidor de mídia
          </h3>
          {q.data && (
            <StatusBadge tone={q.data.status === 'up' ? 'ok' : 'warn'}>
              {q.data.status === 'up' ? 'Disponível' : 'Indisponível'}
            </StatusBadge>
          )}
        </div>
        <p className="text-xs text-fg-subtle" aria-live="polite">
          {q.data?.node && <span className="font-mono">ZLMediaKit {q.data.node.version} · </span>}
          {visible ? 'atualiza a cada 5 s' : 'pausado em segundo plano'}
        </p>
      </div>
      {q.data && q.data.nodes.length > 0 && (q.data.nodes.length > 1 || canNodes) && (
        <ul
          aria-label="Máquinas de vídeo"
          className="flex flex-wrap gap-x-4 gap-y-1.5 border-b border-line px-4 py-2 text-xs"
        >
          {q.data.nodes.map((n) => (
            <li key={n.id} className="flex items-center gap-1.5">
              {canNodes ? (
                <Link
                  to={`/media-nodes/${encodeURIComponent(n.id)}`}
                  className="text-fg underline-offset-2 hover:underline"
                >
                  {n.name}
                </Link>
              ) : (
                <span className="text-fg">{n.name}</span>
              )}
              <StatusBadge tone={n.status === 'up' ? 'ok' : 'warn'}>
                {n.status === 'up' ? 'Disponível' : 'Indisponível'}
              </StatusBadge>
            </li>
          ))}
        </ul>
      )}
      {stuck.length > 0 && (
        <ul
          aria-label="Avisos das máquinas de vídeo"
          className="space-y-1 border-b border-line bg-crit-soft px-4 py-2 text-xs text-fg"
        >
          {stuck.map((n) => (
            <li key={n.id}>
              Gravações de <span className="font-medium">{n.name}</span> não estão saindo da
              máquina.{' '}
              <Link
                to={`/media-nodes/${encodeURIComponent(n.id)}`}
                className="text-accent underline-offset-2 hover:underline"
              >
                Ver a máquina
              </Link>
            </li>
          ))}
        </ul>
      )}
      <Body q={q} history={history} />
    </section>
  );
}

function Body({ q, history }: { q: UseQueryResult<MediaStatus>; history: Sample[] }) {
  if (q.isPending) return <PanelSkeleton />;
  if (q.isError && !q.data) {
    return (
      <EmptyState
        tone="crit"
        icon={ServerCrash}
        title="Não foi possível carregar o uso do servidor de mídia"
        description="A API não respondeu a esta consulta. As demais verificações acima continuam valendo."
        action={
          <Button
            variant="primary"
            onClick={() => {
              void q.refetch();
            }}
          >
            Tentar novamente
          </Button>
        }
      />
    );
  }
  const d = q.data;
  if (d.status === 'down' || !d.node) {
    return (
      <EmptyState
        tone="crit"
        icon={ServerCrash}
        title="Servidor de mídia indisponível"
        description="O ZLMediaKit não respondeu. Vídeo ao vivo e gravação ficam parados até ele voltar; o restante da plataforma segue funcionando."
      />
    );
  }
  const node = d.node;
  const mineBps = d.streams.reduce((a, s) => a + s.bytes_per_second, 0);
  const mineViewers = d.streams.reduce((a, s) => a + s.viewers, 0);
  const ev = summarizeLoops(node.event_loops);
  const wk = summarizeLoops(node.work_loops);
  const tone = loadTone(ev.max);

  return (
    <>
      <div className="grid grid-cols-2 gap-px border-b border-line bg-line lg:grid-cols-4">
        <Stat label="Streams ativos" value={String(d.streams_total ?? d.streams.length)}>
          {node.streams} no servidor
        </Stat>
        <Stat label="Espectadores" value={String(mineViewers)}>
          <Sparkline
            label="Espectadores nos últimos minutos"
            points={history.map((s) => ({ at: s.at, value: s.viewers }))}
            slots={historySize}
            format={(v) => `${String(v)} espectadores`}
          />
        </Stat>
        <Stat label="Banda de entrada" value={formatBitrate(mineBps)}>
          <Sparkline
            label="Banda de entrada nos últimos minutos"
            points={history.map((s) => ({ at: s.at, value: s.bytesPerSecond }))}
            slots={historySize}
            format={formatBitrate}
          />
        </Stat>
        <Stat
          label="Carga do servidor"
          value={`${String(ev.max)}%`}
          badge={<StatusBadge tone={tone}>{loadLabel[tone]}</StatusBadge>}
        >
          média {ev.avg}% · atraso máx. {ev.maxDelay} ms · {node.connections} conexões
        </Stat>
      </div>

      <div className="grid gap-x-8 gap-y-4 border-b border-line px-4 py-4 lg:grid-cols-2">
        <Loops
          title="Rede"
          hint="Laços de eventos: recebem câmeras e entregam vídeo."
          loops={node.event_loops}
        />
        <Loops
          title="Processamento"
          hint={`Tarefas de apoio (hooks, arquivos) · média ${String(wk.avg)}%.`}
          loops={node.work_loops}
        />
      </div>

      <StreamsTable status={d} />
      <ViewersSection />
      <p className="border-t border-line px-4 py-2 text-2xs text-fg-subtle">
        Totais “no servidor” somam tudo o que as máquinas de vídeo atendem. Última leitura{' '}
        {timeFormat.format(new Date(d.checked_at))}.
      </p>
    </>
  );
}

function Stat({
  label,
  value,
  badge,
  children,
}: {
  label: string;
  value: string;
  badge?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0 bg-surface px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-2xs tracking-wide text-fg-subtle uppercase">{label}</span>
        {badge}
      </div>
      <div className="mt-1 text-xl font-semibold text-fg tabular-nums">{value}</div>
      <div className="mt-1 min-h-6 text-xs text-fg-muted">{children}</div>
    </div>
  );
}

function Loops({ title, hint, loops }: { title: string; hint: string; loops: ThreadLoad[] }) {
  return (
    <div>
      <h4 className="text-2xs font-medium tracking-wide text-fg-subtle uppercase">{title}</h4>
      <p className="mt-0.5 mb-2 text-xs text-fg-muted">{hint}</p>
      <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-4">
        {loops.map((l, i) => {
          const tone = loadTone(l.load);
          return (
            <li key={l.name} className="min-w-0">
              <div className="flex justify-between text-2xs text-fg-muted">
                <span title={l.name}>#{i + 1}</span>
                <span className="tabular-nums">
                  {l.load}%{tone !== 'ok' && ` · ${loadLabel[tone]}`}
                </span>
              </div>
              <div
                role="meter"
                aria-label={`${title} ${String(i + 1)}`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={l.load}
                aria-valuetext={`${String(l.load)}%, ${loadLabel[tone]}, atraso ${String(l.delay_ms)} ms`}
                className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-surface-hover"
              >
                <div
                  className={`h-full rounded-full ${
                    tone === 'crit' ? 'bg-crit' : tone === 'warn' ? 'bg-warn' : 'bg-accent'
                  }`}
                  style={{ width: `${String(Math.max(l.load, l.load > 0 ? 3 : 0))}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function StreamsTable({ status }: { status: MediaStatus }) {
  const capped = shownOf(status.streams.length, status.streams_total, 'streams');
  if (status.streams.length === 0) {
    return (
      <EmptyState
        icon={Radio}
        title="Nenhum stream ativo"
        description="Os streams abrem sob demanda quando alguém assiste ou uma gravação está ativa, e fecham 30 s após o último espectador."
      />
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-[13px]">
        <caption className="sr-only">Streams ativos das câmeras das empresas</caption>
        <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
          <tr className="border-b border-line">
            <th scope="col" className="px-4 py-2 font-medium">
              Empresa
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              Câmera
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              Máquina
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              Vídeo
            </th>
            <th scope="col" className="px-4 py-2 text-right font-medium">
              Espectadores
            </th>
            <th scope="col" className="px-4 py-2 text-right font-medium">
              Entrada
            </th>
            <th scope="col" className="px-4 py-2 text-right font-medium">
              Recebido
            </th>
            <th scope="col" className="px-4 py-2 text-right font-medium">
              Ativo há
            </th>
          </tr>
        </thead>
        <tbody>
          {status.streams.map((s) => (
            <tr
              key={`${s.device.id}-${s.profile_token ?? 'stale'}-${s.protocols.join()}`}
              className="border-b border-line last:border-0"
            >
              <td className="px-4 py-2.5 text-fg">{tenantName(s.tenant)}</td>
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-2 text-fg">
                  {s.device.name}
                  {s.recording && (
                    <span className="inline-flex items-center gap-1 text-2xs font-medium text-crit">
                      <Circle aria-hidden="true" className="size-2 fill-current" strokeWidth={0} />
                      Gravando
                    </span>
                  )}
                </div>
                <div className="text-2xs text-fg-subtle">
                  {s.device.site} ·{' '}
                  {s.profile_token ? s.profile_name || s.profile_token : 'perfil removido'}
                </div>
              </td>
              <td className="px-4 py-2.5 text-xs text-fg-muted">{s.node.name || '—'}</td>
              <td className="px-4 py-2.5 font-mono text-xs text-fg-muted">
                {s.video
                  ? `${s.video.codec} · ${String(s.video.width)}×${String(s.video.height)}${
                      s.video.frame_rate ? ` · ${String(Math.round(s.video.frame_rate))} fps` : ''
                    }`
                  : 'aguardando vídeo'}
                {s.audio && <span className="text-fg-subtle"> · {s.audio}</span>}
              </td>
              <td className="px-4 py-2.5 text-right text-fg tabular-nums">{s.viewers}</td>
              <td className="px-4 py-2.5 text-right font-mono text-xs text-fg-muted tabular-nums">
                {formatBitrate(s.bytes_per_second)}
              </td>
              <td className="px-4 py-2.5 text-right font-mono text-xs text-fg-muted tabular-nums">
                {formatBytes(s.total_bytes)}
              </td>
              <td className="px-4 py-2.5 text-right text-xs text-fg-muted tabular-nums">
                {formatUptime(s.alive_seconds)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {capped && (
        <p role="note" className="border-t border-line px-4 py-2 text-2xs text-fg-muted">
          {capped}
        </p>
      )}
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div aria-hidden="true" className="grid grid-cols-2 gap-4 p-4 lg:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="space-y-2">
          <div className="h-2.5 w-20 animate-pulse rounded bg-surface-hover" />
          <div className="h-5 w-16 animate-pulse rounded bg-surface-hover" />
          <div className="h-2.5 w-28 animate-pulse rounded bg-surface-hover" />
        </div>
      ))}
    </div>
  );
}

/**
 * Who is watching the cameras now (every tenant's for the super
 * administrator, 0.67.0, with the tenant on each row): people (live, replay of
 * device recordings), other systems and players the platform did not grant.
 * Internal readers (relays, recording) are left out.
 */
function ViewersSection() {
  const { request } = useAuth();
  const fmt = useTimeFormat();
  const visible = usePageVisible(0);
  const q = useQuery({
    queryKey: mediaViewersKey,
    queryFn: () => getMediaViewers(request),
    refetchInterval: visible ? refreshMs * 2 : false,
    refetchIntervalInBackground: false,
  });
  const rows = (q.data?.items ?? []).flatMap((c) => c.viewers.map((v) => ({ camera: c, v })));
  const capped = shownOf(q.data?.items.length ?? 0, q.data?.total, 'câmeras assistidas');
  // Who is consuming what: one chip per person or system.
  const summary = new Map<string, { label: string; cameras: Set<string>; sessions: number }>();
  for (const { camera, v } of rows) {
    const label = viewerName(v);
    const e = summary.get(label) ?? { label, cameras: new Set<string>(), sessions: 0 };
    e.cameras.add(camera.device.id);
    e.sessions++;
    summary.set(label, e);
  }
  return (
    <section aria-labelledby="viewers-heading" className="border-t border-line">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 pt-4 pb-2">
        <h4
          id="viewers-heading"
          className="text-2xs font-medium tracking-wide text-fg-subtle uppercase"
        >
          Quem está assistindo
        </h4>
        <span className="text-2xs text-fg-subtle">
          {rows.length} {rows.length === 1 ? 'sessão' : 'sessões'} · gravação e repasses internos
          não contam
        </span>
      </div>
      {q.isPending ? (
        <p role="status" className="px-4 pb-4 text-xs text-fg-muted">
          Carregando…
        </p>
      ) : q.isError ? (
        <p role="alert" className="px-4 pb-4 text-xs text-crit">
          Não foi possível carregar os espectadores.
        </p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Eye}
          title="Ninguém assistindo agora"
          description="As sessões de ao vivo, reprodução no equipamento e integrações aparecem aqui enquanto estiverem abertas."
        />
      ) : (
        <>
          <ul aria-label="Resumo por pessoa ou sistema" className="flex flex-wrap gap-2 px-4 pb-3">
            {[...summary.values()].map((e) => (
              <li
                key={e.label}
                className="rounded-full border border-line-strong bg-canvas px-2.5 py-0.5 text-xs text-fg"
              >
                {e.label}{' '}
                <span className="text-fg-subtle">
                  · {e.cameras.size} {e.cameras.size === 1 ? 'câmera' : 'câmeras'}
                  {e.sessions > e.cameras.size && ` · ${String(e.sessions)} sessões`}
                </span>
              </li>
            ))}
          </ul>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px]">
              <caption className="sr-only">Sessões abertas nas câmeras das empresas</caption>
              <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
                <tr className="border-y border-line">
                  {[
                    'Empresa',
                    'Câmera',
                    'Máquina',
                    'Quem',
                    'Origem',
                    'Transporte',
                    'Vídeo',
                    'IP',
                    'Navegador',
                    'Desde',
                  ].map((h) => (
                    <th key={h} scope="col" className="px-4 py-2 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ camera, v }, i) => (
                  <tr
                    key={`${camera.device.id}-${String(i)}`}
                    className="border-b border-line last:border-0"
                  >
                    <td className="px-4 py-2.5 text-fg">{tenantName(camera.tenant)}</td>
                    <td className="px-4 py-2.5">
                      <div className="text-fg">{camera.device.name}</div>
                      <div className="text-2xs text-fg-subtle">{camera.device.site}</div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-fg-muted">
                      {camera.node || '—'}
                      {v.via && <div className="text-2xs text-fg-subtle">via máquina {v.via}</div>}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className={v.kind === 'unknown' ? 'text-warn' : 'text-fg'}>
                        {viewerName(v)}
                      </div>
                      {v.user_email && (
                        <div className="text-2xs text-fg-subtle">{v.user_email}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-fg-muted">{viewerKindLabels[v.kind]}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-fg-muted">
                      {transportLabels[v.transport] ?? v.transport}
                    </td>
                    <td className="px-4 py-2.5 text-fg-muted">
                      {v.sub ? 'Secundário' : 'Principal'}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-fg-muted">{v.ip ?? '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-fg-muted">
                      {describeAgent(v.user_agent)}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-fg-muted">
                      {v.since ? fmt.relative(v.since) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {capped && (
            <p role="note" className="border-t border-line px-4 py-2 text-2xs text-fg-muted">
              {capped}
            </p>
          )}
          <p className="border-t border-line px-4 py-2 text-2xs text-fg-subtle">
            IP e navegador são dados pessoais: visíveis só para quem acompanha o sistema. Atrás de
            NAT ou de encaminhamento de porta, o IP pode ser o do roteador.
          </p>
        </>
      )}
    </section>
  );
}
